import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import Navbar from "@/components/navbar";
import CountdownTimer from "@/components/countdown-timer";
import { getSATime } from "@shared/timezone";
import { 
  Trophy, 
  Play, 
  Clock, 
  Gift,
  Users,
  ArrowRight,
  Calendar,
  Filter,
  Search,
  Lock,
  CheckCircle
} from "lucide-react";

interface Competition {
  id: number;
  title: string;
  description: string;
  prize?: string;
  prize1?: string;
  prize2?: string;
  prize3?: string;
  startDate: string;
  startTime?: string;
  endDate: string;
  endTime?: string;
  type: string;
  category: string;
  bannerImageUrl?: string;
  isActive: boolean;
  hasEntered?: boolean;
  status?: string;
}

function CompetitionCard({ competition }: { competition: Competition }) {
  const { user } = useAuth();
  
  // Check if user has entered this competition
  const { data: userEntries } = useQuery<any>({
    queryKey: ["/api/user/entries", competition.id],
    enabled: !!user,
    staleTime: 30000,
  });
  
  // Calculate competition timing status
  const now = new Date();
  const startDateOnly = new Date(competition.startDate).toISOString().split("T")[0];
  const startTimeStr = competition.startTime || "00:00";
  const startDateTime = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`);
  
  const endDateOnly = new Date(competition.endDate).toISOString().split("T")[0];
  const endTimeStr = competition.endTime || "23:59";
  const endDateTime = new Date(`${endDateOnly}T${endTimeStr}:59+02:00`);
  
  const isExpired = endDateTime < now;
  const isScheduled = startDateTime > now;
  const isActive = !isExpired && !isScheduled;
  
  // Check if user has entered this competition
  const hasEntered = userEntries && (
    (typeof userEntries === 'object' && 'totalEntries' in userEntries && userEntries.totalEntries > 0) ||
    (Array.isArray(userEntries) && userEntries.length > 0) ||
    (userEntries && typeof userEntries === 'object' && 'entries' in userEntries && 
     Array.isArray(userEntries.entries) && userEntries.entries.length > 0)
  );
  

  const getPrimaryPrize = () => {
    return competition.prize1 || competition.prize || "Amazing Prize";
  };

  const getStatusBadge = () => {
    if (isExpired) {
      return <Badge variant="secondary" className="text-xs">Ended</Badge>;
    }
    if (isScheduled) {
      return <Badge variant="outline" className="text-xs">Scheduled</Badge>;
    }
    return <Badge variant="default" className="text-xs bg-green-600">Active</Badge>;
  };

  return (
    <Card 
      className="hover:shadow-md transition-shadow duration-200 border-2 hover:border-blue-200"
      data-testid={`card-competition-${competition.id}`}
    >
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 pr-3">
              <h3 className="font-semibold text-sm line-clamp-2 mb-1">
                {competition.title}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                {competition.description}
              </p>
            </div>
            {getStatusBadge()}
          </div>

          {/* Prize Section */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-3 mb-3">
            <div className="flex items-center mb-2">
              <Gift className="h-4 w-4 text-yellow-600 mr-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Prize</span>
            </div>
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              🏆 {getPrimaryPrize()}
            </p>
            
            {/* Additional prizes */}
            {(competition.prize2 || competition.prize3) && (
              <div className="mt-2 space-y-1">
                {competition.prize2 && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    🥈 {competition.prize2}
                  </p>
                )}
                {competition.prize3 && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    🥉 {competition.prize3}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Timing and Actions */}
          <div className="space-y-3">
            {isActive && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600 dark:text-gray-400">Ends in:</span>
                <div className="text-xs font-medium">
                  <CountdownTimer 
                    endDate={competition.endDate}
                    endTime={competition.endTime}
                    compact={true}
                  />
                </div>
              </div>
            )}

            {isScheduled && (
              <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                <Calendar className="h-3 w-3 mr-1" />
                Starts: {startDateTime.toLocaleDateString()}
              </div>
            )}

            {user ? (
              // Logged in - navigate to competition detail
              <Link href={`/m/competition/${competition.id}`}>
                <Button
                  className={`w-full h-12 ${
                    isExpired
                      ? 'bg-gray-400 hover:bg-gray-500'
                      : isScheduled
                        ? 'bg-blue-500 hover:bg-blue-600'
                        : hasEntered
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  data-testid={`button-enter-${competition.id}`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    {isExpired ? (
                      <>
                        <Trophy className="h-4 w-4" />
                        <span>View Results</span>
                      </>
                    ) : isScheduled ? (
                      <>
                        <Clock className="h-4 w-4" />
                        <span>View Details</span>
                      </>
                    ) : hasEntered ? (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        <span>Entered</span>
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4" />
                        <span>Enter Now</span>
                      </>
                    )}
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </Button>
              </Link>
            ) : (
              // Logged out - redirect to auth with signup tab
              <Link href="/auth?tab=signup">
                <Button
                  className={`w-full h-12 ${
                    isExpired
                      ? 'bg-gray-400 hover:bg-gray-500'
                      : isScheduled
                        ? 'bg-blue-500 hover:bg-blue-600'
                        : 'hover:bg-red-600'
                  }`}
                  style={{
                    backgroundColor: !isExpired && !isScheduled ? '#EF4444' : undefined
                  }}
                  data-testid={`button-enter-${competition.id}`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Lock className="h-4 w-4" />
                    <span>Enter Now</span>
                  </div>
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
  );
}

function CompetitionsSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i}>
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between items-start">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
              </div>
              <Skeleton className="h-6 w-16" />
            </div>
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-12 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function MobileCompetitions() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"active" | "past">("active");

  // Fetch active competitions
  const { data: activeCompetitions, isLoading: isLoadingActive } = useQuery<Competition[]>({
    queryKey: ["/api/competitions"],
  });

  // Fetch past competitions  
  const { data: pastCompetitions, isLoading: isLoadingPast } = useQuery<Competition[]>({
    queryKey: ["/api/competitions/past"],
  });

  const isLoading = isLoadingActive || isLoadingPast;

  const currentCompetitions = activeTab === "active" 
    ? (activeCompetitions || [])
    : (pastCompetitions || []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Show Navbar for logged-out users */}
      {!user && <Navbar />}
      {/* Mobile Header */}
      <div className="text-white" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="px-4 pt-12 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3 flex-1">
              <div>
                <h1 className="text-2xl font-bold">Competitions</h1>
                <p className="text-blue-100 text-sm">Enter to win amazing prizes</p>
              </div>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Trophy className="h-6 w-6" />
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex bg-white/20 rounded-lg p-1">
            <button
              onClick={() => setActiveTab("active")}
              data-testid="tab-active-competitions"
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === "active"
                  ? "bg-white text-blue-600"
                  : "text-white hover:bg-white/10"
              }`}
            >
              Active ({activeCompetitions?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab("past")}
              data-testid="tab-past-competitions"
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === "past"
                  ? "bg-white text-blue-600"
                  : "text-white hover:bg-white/10"
              }`}
            >
              Past ({pastCompetitions?.length || 0})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {isLoading ? (
          <CompetitionsSkeleton />
        ) : currentCompetitions.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No {activeTab} competitions
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {activeTab === "active" 
                ? "Check back later for new competitions!" 
                : "No completed competitions yet."}
            </p>
            {activeTab === "past" && (
              <Button 
                onClick={() => setActiveTab("active")}
                data-testid="button-view-active"
              >
                View Active Competitions
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {currentCompetitions.map((competition: Competition) => (
              <CompetitionCard 
                key={competition.id} 
                competition={competition} 
              />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}